/**
 * Estado local do wizard de criacao/edicao de Plano de Cobranca.
 * Reflete os campos do schema mas com strings para permitir edicao livre
 * dos inputs numericos (decimal). Conversao acontece no submit.
 */

import type {
  ChargePlanDocumento,
  ChargePlanDocumentoTipo,
  ChargePlanInstallment,
  ChargePlanNotificacaoSlot,
  ChargePlanRenovacaoIncrementoTipo,
  ChargePlanStatus,
  ChargePlanTipoMulta,
  ChargePlanTipoValor,
  OnboardingConfig,
} from "../../../services/charge-plans.service";
import {
  CCB_TEMPLATE,
  DEFAULT_CONTRACT_TEMPLATE,
  NOTA_PROMISSORIA_TEMPLATE,
} from "./contract-templates";

export type NotificacaoKey = "organizacao" | "parcela" | "ultimo";

export interface PlanFormState {
  // Geral
  nome: string;
  codigo: string;
  status: ChargePlanStatus;

  // Etapa 1 - Faca seu cliente cadastrar online direto pelo app
  onboardingConfig: OnboardingConfig;

  // Etapa 2 - Quem define o valor
  tipoValor: ChargePlanTipoValor;
  valorFixo: string;

  // Etapa 3 - Regras
  valorMinimo: string;
  valorMaximo: string;
  /**
   * Incremento aplicado a cada renovacao bem-sucedida — SO se usa quando
   * tipoValor === "fixo" (modo "Eu defino o valor") E incrementoHabilitado === true.
   * Caso contrario o incremento e ignorado.
   */
  valorStep: string;
  /**
   * Quando true: cobranca comeca no minimo e sobe `valorStep` a cada renovacao.
   * Quando false: cobranca fica no minimo (sem progressao).
   * So aplica quando tipoValor === "fixo".
   */
  incrementoHabilitado: boolean;

  maxActiveCharges: number;
  renovacaoIncrementoTipo: ChargePlanRenovacaoIncrementoTipo;
  renovacaoIncrementoValor: string;

  diasSemana: number[]; // 0=Dom, 1=Seg, ..., 6=Sab
  incluirFeriados: boolean;

  renovacaoHabilitada: boolean;
  /**
   * Discriminador do gate de progresso de renovacao:
   *  - "parcelas": usa renovacaoParcelasMinPagas (n minimo de parcelas pagas).
   *  - "percentual": usa renovacaoPercentualPagoMin (% do contrato em R$ pago).
   * O outro campo eh persistido como null pelo wizard ao normalizar o payload.
   */
  renovacaoProgressoTipo: "parcelas" | "percentual";
  renovacaoParcelasMinPagas: string;
  renovacaoPercentualPagoMin: string;
  renovacaoDiasMin: string;
  renovacaoMultasMax: string;
  renovacaoMultasAbertasMax: string;

  tipoMulta: ChargePlanTipoMulta;
  multaPercentual: string;
  multaValorFixo: string;
  multaAplicarSobre: string;
  multaExplicacao: string;

  // Etapa 4 - Parcelas
  installments: ChargePlanInstallment[];

  // Etapa 6 - Mensagem geral + 3 notificacoes push automaticas
  mensagemCliente: string;
  notificacoes: Record<NotificacaoKey, ChargePlanNotificacaoSlot>;

  // Etapa 7 - Documentos para assinatura digital
  documentos: ChargePlanDocumento[];
  validacaoParceiroHabilitada: boolean;
}

export const DEFAULT_DOCUMENTOS: ChargePlanDocumento[] = [
  {
    tipo: "ccb",
    incluir: false,
    nomeInterno: "CCB",
    tituloCliente: "Cédula de Crédito Bancário",
    mensagem: "",
    observacao: "",
    arquivoStorageKey: null,
    templateHtml: CCB_TEMPLATE,
  },
  {
    tipo: "contrato",
    incluir: true,
    nomeInterno: "Contrato",
    tituloCliente: "Contrato de Prestacao de Servico",
    mensagem:
      "Por favor, leia atentamente o documento abaixo. Ao concordar com os termos, assine digitalmente para confirmar.",
    observacao: "",
    arquivoStorageKey: null,
    templateHtml: DEFAULT_CONTRACT_TEMPLATE,
  },
  {
    tipo: "nota_promissoria_digital",
    incluir: false,
    nomeInterno: "Nota promissoria digital",
    tituloCliente: "Nota Promissoria Digital",
    mensagem: "",
    observacao: "",
    arquivoStorageKey: null,
    templateHtml: NOTA_PROMISSORIA_TEMPLATE,
  },
];

export const DOCUMENTO_META: Record<
  ChargePlanDocumentoTipo,
  { titulo: string; descricao: string; icon: string; tone: string }
> = {
  ccb: {
    titulo: "CCB",
    descricao: "Cédula de crédito bancário.",
    icon: "🏦",
    tone: "bg-emerald-50 text-emerald-700",
  },
  contrato: {
    titulo: "Contrato",
    descricao: "Documento com as condicoes acordadas entre as partes.",
    icon: "📄",
    tone: "bg-primary-50 text-primary-700",
  },
  nota_promissoria_digital: {
    titulo: "Nota promissoria digital",
    descricao: "Documento que registra o compromisso de pagamento.",
    icon: "✍️",
    tone: "bg-amber-50 text-amber-700",
  },
};

/**
 * Default "Modelo pronto" para Step 1: o gestor parte de uma config sensata e
 * customiza so o que precisa. Reflete o caso mais comum (KYC com Pix).
 */
export function makeDefaultOnboardingConfig(): OnboardingConfig {
  return {
    modeloPronto: true,
    dadosPessoais: {
      habilitado: true,
      cpfNomeDn: true,
      cep: true,
      enderecoCompleto: true,
    },
    contatos: {
      habilitado: true,
      whatsapp: true,
      email: true,
    },
    referencias: {
      habilitado: true,
      quantidade: 2,
    },
    documentos: {
      habilitado: true,
      lista: [
        { id: "rg", nome: "RG", label: "" },
        { id: "cpf", nome: "CPF", label: "" },
      ],
    },
    // Fotos/videos adicionais sao independentes do Card 4 (Documentos).
    // Comecamos com 2 exemplos editaveis/removiveis para o gestor entender
    // o padrao; ele adiciona os demais via "+ Adicionar item".
    fotosVideos: {
      habilitado: true,
      lista: [
        {
          id: "comprovante_residencia",
          nome: "Foto do comprovante de residencia",
          exemplo: "Ex: Conta de luz, agua ou telefone",
          quantidade: 1,
          ordem: 1,
          tipo: "foto",
          obrigatorio: false,
        },
        {
          id: "selfie_documento",
          nome: "Selfie segurando o documento",
          exemplo: "Foto do rosto segurando o documento ao lado",
          quantidade: 1,
          ordem: 2,
          tipo: "selfie",
          obrigatorio: false,
        },
      ],
    },
    chavePix: { habilitado: true },
    customFlowSteps: [],
  };
}

export const DEFAULT_NOTIF_TEXTS: Record<NotificacaoKey, string> = {
  organizacao:
    "Ola, {nome_cliente}! 👋\nSo passando para lembrar de se organizar com seus compromissos. Manter tudo em dia e a chave para ter tranquilidade! 💪\nConte com a gente! 😊",
  parcela:
    "Ola, {nome_cliente}!\nIdentificamos que voce ainda nao realizou o pagamento da sua parcela.\nValor: {valor_parcela}\nVencimento: {data_vencimento}\nFique atento para manter seus compromissos em dia.\nQualquer duvida, estamos a disposicao!",
  ultimo:
    "Ola, {nome_cliente}!\nAinda nao identificamos o pagamento da sua parcela.\nValor: {valor_parcela}\nVencimento: {data_vencimento}\nEvite imprevistos e mantenha seus compromissos em dia.\nObrigado! 🙌",
};

export const DEFAULT_PLAN: PlanFormState = {
  nome: "",
  codigo: "",
  status: "rascunho",

  onboardingConfig: makeDefaultOnboardingConfig(),

  tipoValor: "fixo",
  valorFixo: "",

  valorMinimo: "300",
  valorMaximo: "10000",
  valorStep: "100",
  incrementoHabilitado: true,
  maxActiveCharges: 1,
  renovacaoIncrementoTipo: "nenhum",
  renovacaoIncrementoValor: "0",

  diasSemana: [1, 2, 3, 4, 5],
  incluirFeriados: true,

  renovacaoHabilitada: true,
  renovacaoProgressoTipo: "parcelas",
  renovacaoParcelasMinPagas: "5",
  renovacaoPercentualPagoMin: "50",
  renovacaoDiasMin: "3",
  renovacaoMultasMax: "5",
  renovacaoMultasAbertasMax: "2",

  tipoMulta: "parcela_extra",
  multaPercentual: "2",
  multaValorFixo: "",
  multaAplicarSobre: "sobre_toda_divida",
  multaExplicacao: "Sera cobrada 1 parcela extra em caso de atraso no pagamento.",

  installments: [],

  mensagemCliente: "",
  notificacoes: {
    organizacao: { ativo: true, texto: DEFAULT_NOTIF_TEXTS.organizacao },
    parcela: { ativo: true, texto: DEFAULT_NOTIF_TEXTS.parcela },
    ultimo: { ativo: true, texto: DEFAULT_NOTIF_TEXTS.ultimo },
  },
  documentos: DEFAULT_DOCUMENTOS.map((d) => ({ ...d })),
  validacaoParceiroHabilitada: false,
};

export type WizardStep = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
export const LAST_STEP: WizardStep = 8;

export const STEPS: { id: WizardStep; label: string; available: boolean }[] = [
  { id: 1, label: "Dados do cliente", available: true },
  { id: 2, label: "Quem define o valor", available: true },
  // Ordem invertida na UI: parcelas vem antes de regras.
  { id: 3, label: "Parcelas e prazos", available: true },
  { id: 4, label: "Regras da cobranca", available: true },
  { id: 5, label: "Multas e juros", available: true },
  { id: 6, label: "Notificacoes do aplicativo", available: true },
  { id: 7, label: "Documentos para assinatura digital", available: true },
  { id: 8, label: "Revisao e ativacao", available: true },
];

/**
 * Mapeamento: step interno (1..8) -> step visual exibido no indicador
 * (1=Dados do cliente, 2=Plano e regras, 3=Assinatura digital, 4=Revisao e ativacao).
 */
export type VisualWizardStep = 1 | 2 | 3 | 4;
export function toVisualStep(step: WizardStep): VisualWizardStep {
  if (step === 1) return 1;
  if (step >= 2 && step <= 6) return 2;
  if (step === 7) return 3;
  return 4;
}
